import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, User, Star, Search, Filter, MessageCircle, BarChart2, TrendingUp, Send, MoreHorizontal } from 'lucide-react';

const C = {
  bg: 'var(--pd-bg)', bg2: 'var(--pd-bg2)', surface: 'var(--pd-surface)', surface2: 'var(--pd-surface2)', surface3: 'var(--pd-surface3)',
  border: 'var(--pd-border)', text: 'var(--pd-text)', text2: 'var(--pd-text2)', muted: 'var(--pd-muted)',
  purple: 'var(--pd-purple)', cyan: 'var(--pd-cyan)', amber: 'var(--pd-amber)', green: 'var(--pd-green)', red: 'var(--pd-red)',
  gold: 'var(--pd-gold)'
};

const MockProfile = () => (
  <div className="w-full h-full flex items-center justify-center p-8 bg-black/40">
    <div className="w-full max-w-sm rounded-[1.5rem] p-6" style={{ background: 'var(--pd-nav-bg)', backdropFilter: 'blur(20px)', border: '1px solid var(--pd-border)' }}>
      <div className="flex flex-col items-center text-center">
        <div className="w-24 h-24 rounded-full bg-gradient-to-tr from-purple-500 to-cyan-500 p-1 mb-4">
          <div className="w-full h-full bg-black rounded-full flex items-center justify-center">
            <User className="w-10 h-10 text-white/50" />
          </div>
        </div>
        <h4 className="text-xl font-bold text-white mb-1">Alex Chen</h4>
        <p className="text-sm text-purple-400 font-medium mb-4">Tech Entrepreneur & Host</p>
        <div className="w-full grid grid-cols-2 gap-3 mb-6 text-sm">
          <div className="bg-white/5 rounded-lg py-2 flex flex-col items-center">
            <span className="text-white font-bold">12k</span>
            <span className="text-white/50 text-[10px] uppercase tracking-wider">Listeners</span>
          </div>
          <div className="bg-white/5 rounded-lg py-2 flex flex-col items-center">
            <span className="flex items-center gap-1 text-white font-bold"><Star className="w-3 h-3 text-yellow-500" fill="currentColor"/> 4.9</span>
            <span className="text-white/50 text-[10px] uppercase tracking-wider">Rating</span>
          </div>
        </div>
        <div className="w-full h-10 rounded-full bg-purple-600 flex items-center justify-center text-sm font-semibold text-white">
          View Full Profile
        </div>
      </div>
    </div>
  </div>
);

const MockDirectory = () => (
  <div className="w-full h-full flex flex-col p-6 bg-black/40">
    <div className="w-full h-12 rounded-xl bg-white/10 border border-white/5 flex items-center px-4 mb-6 mt-4">
      <Search className="w-5 h-5 text-white/40 mr-3" />
      <div className="h-4 w-32 bg-white/20 rounded-full"></div>
      <div className="ml-auto flex items-center gap-2">
        <div className="w-px h-6 bg-white/10"></div>
        <Filter className="w-5 h-5 text-white/40 ml-2" />
      </div>
    </div>
    <div className="flex gap-2 mb-6">
      <div className="px-4 py-1.5 rounded-full bg-cyan-500/20 text-cyan-400 text-xs font-semibold border border-cyan-500/30">Technology</div>
      <div className="px-4 py-1.5 rounded-full bg-white/5 text-white/60 text-xs font-semibold border border-white/10">Business</div>
      <div className="px-4 py-1.5 rounded-full bg-white/5 text-white/60 text-xs font-semibold border border-white/10">Marketing</div>
    </div>
    <div className="flex-1 flex flex-col gap-3">
      {[1, 2, 3].map(i => (
        <div key={i} className="w-full p-3 rounded-xl bg-white/5 border border-white/5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-white/10 shrink-0"></div>
          <div className="flex-1">
            <div className="h-3 w-24 bg-white/30 rounded-full mb-2"></div>
            <div className="h-2 w-32 bg-white/10 rounded-full"></div>
          </div>
          <div className="px-3 py-1 rounded-full bg-white/10 text-[10px] text-white/60">Match</div>
        </div>
      ))}
    </div>
  </div>
);

const MockChat = () => (
  <div className="w-full h-full flex flex-col bg-black/40">
    <div className="h-16 border-b border-white/10 flex items-center px-6 bg-white/5">
      <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-green-500 to-cyan-500 mr-3"></div>
      <div>
        <div className="h-3 w-20 bg-white/40 rounded-full mb-1"></div>
        <div className="h-2 w-16 bg-green-400/60 rounded-full"></div>
      </div>
      <MoreHorizontal className="w-5 h-5 text-white/40 ml-auto" />
    </div>
    <div className="flex-1 p-6 flex flex-col gap-4 justify-end">
      <div className="self-start max-w-[80%]">
        <div className="p-3 rounded-2xl rounded-tl-sm bg-white/10 text-xs text-white/80 leading-relaxed border border-white/5">
          Hey! Loved your recent episode on AI. Would love to have you on my show next week!
        </div>
      </div>
      <div className="self-end max-w-[80%]">
        <div className="p-3 rounded-2xl rounded-tr-sm bg-green-600/80 text-xs text-white leading-relaxed border border-green-500/50">
          That sounds great! I'm available Tuesday or Thursday. Let's make it happen.
        </div>
      </div>
      <div className="self-start max-w-[80%]">
        <div className="p-3 rounded-2xl rounded-tl-sm bg-white/10 text-xs text-white/80 leading-relaxed border border-white/5 flex items-center gap-2">
          <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>
          Calendar Invite Sent
        </div>
      </div>
    </div>
    <div className="h-12 m-4 mt-2 rounded-full bg-white/10 border border-white/10 flex items-center px-4">
      <div className="h-3 w-32 bg-white/20 rounded-full"></div>
      <Send className="w-4 h-4 text-white/40 ml-auto" />
    </div>
  </div>
);

const MockDashboard = () => (
  <div className="w-full h-full flex flex-col p-8 bg-black/40">
    <div className="flex justify-between items-end mb-10 mt-4">
      <div>
        <div className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">Total Downloads</div>
        <div className="text-4xl font-bold text-white">124.5k</div>
      </div>
      <div className="flex items-center gap-1 text-amber-400 bg-amber-400/10 px-3 py-1.5 rounded-full text-xs font-bold border border-amber-400/20">
        <TrendingUp className="w-3 h-3" /> +24%
      </div>
    </div>
    <div className="flex-1 flex items-end gap-3 pb-6 border-b border-white/10 relative">
      <div className="absolute top-0 left-0 right-0 h-px bg-white/5 border-dashed"></div>
      <div className="absolute top-1/2 left-0 right-0 h-px bg-white/5 border-dashed"></div>
      {[40, 55, 45, 70, 65, 80, 95, 120].map((h, i) => (
        <div key={i} className="flex-1 bg-gradient-to-t from-amber-500/20 to-amber-500/80 rounded-t-sm transition-all hover:opacity-80" style={{ height: `${(h / 120) * 100}%` }}></div>
      ))}
    </div>
    <div className="flex justify-between mt-4 text-[10px] text-white/40 uppercase font-bold">
      <span>Mon</span>
      <span>Wed</span>
      <span>Fri</span>
      <span>Sun</span>
    </div>
  </div>
);

const StickyHowItWorks = () => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const containerRef = useRef(null);

  const steps = [
    { id: 0, step: 'Step 1', time: '2 mins', title: 'Create Profile', desc: t('home.hostStep1') || 'Set up your directory listing.', color: C.purple, items: ['Basic information', 'Upload avatar', 'Set target audience'], Component: MockProfile },
    { id: 1, step: 'Step 2', time: '5 mins', title: 'Find Matches', desc: t('home.hostStep2') || 'Discover swappers.', color: C.cyan, items: ['Filter by category', 'Check listenership', 'Read reviews'], Component: MockDirectory },
    { id: 2, step: 'Step 3', time: '10 mins', title: 'Pitch & Connect', desc: t('home.hostStep3') || 'Send a message.', color: C.green, items: ['Send direct message', 'Propose a topic', 'Schedule recording'], Component: MockChat },
    { id: 3, step: 'Step 4', time: 'Ongoing', title: 'Record & Grow', desc: t('home.hostStep4') || 'Record the episode together, cross-promote it to your audiences, and watch your downloads soar.', color: C.amber, items: ['Record episode', 'Cross-promote', 'Grow audience'], Component: MockDashboard },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveStep(Number(entry.target.getAttribute('data-step')));
          }
        });
      },
      { rootMargin: '-40% 0px -40% 0px' }
    );

    const stepElements = document.querySelectorAll('.scroll-step');
    stepElements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" className="relative py-16 md:py-32" data-testid="how-it-works-section">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 w-full" ref={containerRef}>
        
        {/* Header Area */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 md:mb-24 gap-4">
          <div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4" style={{ color: C.text }}>
              <span className="text-gradient">{t('home.howItWorks').split(' ')[0]}</span> {t('home.howItWorks').split(' ').slice(1).join(' ')}
            </h2>
            <p className="text-sm sm:text-base max-w-lg" style={{ color: C.text2 }}>{t('home.howItWorksSub')}</p>
          </div>
          <Link to="/auth?mode=register" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium border transition-colors hover:bg-white/5 shrink-0" style={{ color: C.text, borderColor: C.border }}>
            View All Steps <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Sticky Scroll Layout */}
        <div className="flex flex-col md:flex-row gap-12 lg:gap-20 relative">
          
          {/* Left Column: Sticky Visual */}
          <div className="w-full md:w-1/2 lg:w-5/12 hidden md:block relative">
            <div className="sticky top-32 h-[500px] w-full rounded-[2rem] overflow-hidden"
                 style={{ background: 'var(--pd-nav-bg)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid var(--pd-border)', boxShadow: `0 4px 30px rgba(0,0,0,0.3)` }}>
              
              <div className="relative w-full h-full">
                {steps.map((step, idx) => {
                  const isActive = activeStep === idx;
                  const StepComponent = step.Component;
                  return (
                    <div key={idx} 
                         className={`absolute inset-0 transition-all duration-700 ease-in-out
                                     ${isActive ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-105 z-0 pointer-events-none'}`}>
                      <StepComponent />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Column: Scrolling Text */}
          <div className="w-full md:w-1/2 lg:w-7/12">
            <div className="md:pb-[40vh]">
              {steps.map((item, i) => (
                <div key={i} data-step={i} className="scroll-step min-h-[40vh] md:min-h-[60vh] flex flex-col justify-center mb-12 md:mb-0">
                  <div className={`w-full rounded-[2rem] p-6 sm:p-10 relative overflow-hidden transition-all duration-500
                                   ${activeStep === i ? 'opacity-100 scale-100 shadow-2xl' : 'opacity-40 scale-[0.98] md:opacity-30'}`}
                       style={{ background: 'var(--pd-nav-bg)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid var(--pd-border)',
                                boxShadow: activeStep === i ? `0 4px 40px rgba(0,0,0,0.2)` : 'none' }}>
                    
                    {/* Decorative Gradient glow on active card */}
                    <div className={`absolute top-0 left-0 right-0 h-1 transition-opacity duration-500 ${activeStep === i ? 'opacity-100' : 'opacity-0'}`} 
                         style={{ background: `linear-gradient(90deg, ${item.color}80, transparent)` }} />
                    
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                      <div>
                        <span className="text-xs font-bold uppercase tracking-widest block mb-2" style={{ color: item.color }}>{item.step}</span>
                        <h3 className="text-2xl md:text-3xl font-extrabold" style={{ color: C.text }}>{item.title}</h3>
                      </div>
                      <span className="px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider shrink-0" style={{ background: `${item.color}15`, color: item.color }}>
                        {item.time}
                      </span>
                    </div>
                    
                    <p className="text-base md:text-lg leading-relaxed mb-8" style={{ color: C.text2 }}>
                      {item.desc}
                    </p>
                    
                    <div className="w-full h-px bg-white/5 mb-8"></div>

                    <ul className="space-y-4 text-sm font-medium" style={{ color: C.text }}>
                      {item.items.map((listItem, j) => (
                        <li key={j} className="flex items-center gap-4">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: `${item.color}20` }}>
                            <Check className="w-3.5 h-3.5" style={{ color: item.color }} />
                          </div>
                          <span className="text-base">{listItem}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
        </div>
      </div>
    </section>
  );
};

export default StickyHowItWorks;
